import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DocumentSignViewer from '../components/DocumentSignViewer';
import SupplierDocumentUpload from '../components/SupplierDocumentUpload';
import BASE_URL from '../../url';
import './SupplierPortal.css';

// ─── Auth helpers ────────────────────────────────────────────────────────────
const saveSession = (token, user) => {
  localStorage.setItem('supplierToken', token);
  localStorage.setItem('supplierUser', JSON.stringify(user));
};

const clearSession = () => {
  localStorage.removeItem('supplierToken');
  localStorage.removeItem('supplierUser');
};

const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem('supplierUser')); }
  catch { return null; }
};

// ─── Supplier Portal App ─────────────────────────────────────────────────────
const SupplierPortal = () => {
  const [user, setUser] = useState(getStoredUser());
  const [activeTab, setActiveTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [signed, setSigned] = useState([]);
  const [myDocs, setMyDocs] = useState([]);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  // Login form state
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  // ── Auth ────────────────────────────────────────────────────────────────────
  const handleLogin = async e => {
    e.preventDefault();
    setLoginError('');
    setLoggingIn(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/auth/login`, loginData);
      const { token, user: userData } = res.data;
      if (userData.role !== 'supplier') {
        setLoginError('Access denied. This portal is for Suppliers only.');
        return;
      }
      saveSession(token, userData);
      setUser(userData);
    } catch (err) {
      setLoginError(err.response?.data?.error || 'Invalid username or password');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    clearSession();
    setUser(null);
    setPending([]);
    setSigned([]);
    setMyDocs([]);
  };

  // ── Data fetching ───────────────────────────────────────────────────────────
  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('supplierToken')}` }
  });

  const fetchPending = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supplier-portal/pending-signatures`, authHeaders());
      if (res.data.success) setPending(res.data.data);
    } catch (_) {}
  }, []);

  const fetchSigned = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supplier-portal/signed-documents`, authHeaders());
      if (res.data.success) setSigned(res.data.data);
    } catch (_) {}
  }, []);

  const fetchMyDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supplier-portal/my-documents`, authHeaders());
      if (res.data.success) setMyDocs(res.data.data);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([fetchPending(), fetchSigned(), fetchMyDocs()]).finally(() => setLoading(false));
  }, [user, fetchPending, fetchSigned, fetchMyDocs]);

  // ── Login Screen ─────────────────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="sp-login-page">
        <div className="sp-login-card">
          <div className="sp-login-logo">
            <span className="sp-login-logo-icon">🔐</span>
            <h1>Supplier Portal</h1>
            <p>Secure document management &amp; e-signing</p>
          </div>

          <form onSubmit={handleLogin} className="sp-login-form">
            {loginError && <div className="sp-login-error">{loginError}</div>}

            <div className="sp-form-group">
              <label>Username</label>
              <input
                type="text"
                value={loginData.username}
                onChange={e => setLoginData(p => ({ ...p, username: e.target.value }))}
                placeholder="Enter your username"
                autoComplete="username"
                required
              />
            </div>

            <div className="sp-form-group">
              <label>Password</label>
              <input
                type="password"
                value={loginData.password}
                onChange={e => setLoginData(p => ({ ...p, password: e.target.value }))}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
            </div>

            <button type="submit" className="sp-login-btn" disabled={loggingIn}>
              {loggingIn ? 'Signing in…' : 'Login →'}
            </button>
          </form>

          <p className="sp-login-footer">
            Your credentials were sent by email when your account was created.
            Contact your account manager if you need help.
          </p>
        </div>
      </div>
    );
  }

  // ── Dashboard ──────────────────────────────────────────────────────────────
  return (
    <div className="sp-root">
      {/* Top bar */}
      <header className="sp-header">
        <div className="sp-header-left">
          <span className="sp-header-icon">🔐</span>
          <span className="sp-header-title">Supplier Portal</span>
        </div>
        <div className="sp-header-right">
          <span className="sp-header-user">👤 {user.firstName || user.username}</span>
          <button className="sp-logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      <div className="sp-main">
        {/* Tabs */}
        <div className="sp-tabs">
          <button
            className={`sp-tab ${activeTab === 'pending' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            📋 Pending Signatures
            {pending.length > 0 && <span className="sp-tab-badge">{pending.length}</span>}
          </button>
          <button
            className={`sp-tab ${activeTab === 'signed' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('signed')}
          >
            ✅ Signed Documents
          </button>
          <button
            className={`sp-tab ${activeTab === 'upload' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            📁 Upload Documents
          </button>
          <button
            className={`sp-tab ${activeTab === 'my-docs' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('my-docs')}
          >
            🗂 My Documents
          </button>
        </div>

        {/* Tab content */}
        <div className="sp-content">
          {loading && <div className="sp-loading">Loading…</div>}

          {/* Pending Signatures */}
          {!loading && activeTab === 'pending' && (
            <div>
              <h2 className="sp-section-title">Pending Signatures</h2>
              {pending.length === 0 ? (
                <div className="sp-empty-state">
                  <span>✅</span>
                  <p>No pending documents. You're all caught up!</p>
                </div>
              ) : (
                <div className="sp-cards">
                  {pending.map(doc => (
                    <div key={doc.SignRequestId} className="sp-doc-card sp-doc-card-pending">
                      <div className="sp-doc-card-header">
                        <span className="sp-doc-type-badge">{doc.TemplateType}</span>
                        <span className="sp-doc-status sp-status-pending">⏳ PENDING</span>
                      </div>
                      <div className="sp-doc-card-body">
                        <h4>{doc.CompanyName}</h4>
                        <p className="sp-doc-meta">Sent: {new Date(doc.CreatedAtUtc).toLocaleDateString()}</p>
                        <p className="sp-doc-meta sp-doc-expiry">
                          ⏰ Expires: {doc.ExpiresAtUtc ? new Date(doc.ExpiresAtUtc).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      <button
                        className="sp-sign-btn"
                        onClick={() => setViewingDoc(doc)}
                      >
                        ✍️ Sign Now
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Signed Documents */}
          {!loading && activeTab === 'signed' && (
            <div>
              <h2 className="sp-section-title">Signed Documents</h2>
              {signed.length === 0 ? (
                <div className="sp-empty-state">
                  <span>📄</span>
                  <p>No signed documents yet.</p>
                </div>
              ) : (
                <div className="sp-cards">
                  {signed.map(doc => (
                    <div key={doc.SignRequestId} className="sp-doc-card sp-doc-card-signed">
                      <div className="sp-doc-card-header">
                        <span className="sp-doc-type-badge">{doc.TemplateType}</span>
                        <span className="sp-doc-status sp-status-signed">✅ SIGNED</span>
                      </div>
                      <div className="sp-doc-card-body">
                        <h4>{doc.CompanyName}</h4>
                        <p className="sp-doc-meta">Signed: {doc.SignedAtUtc ? new Date(doc.SignedAtUtc).toLocaleDateString() : '—'}</p>
                      </div>
                      {doc.SignedPdfPath && (
                        <a
                          href={`${BASE_URL}/uploads/msa-signed/${doc.SignedPdfPath.split(/[\\/]/).pop()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="sp-view-btn"
                        >
                          👁 View Signed PDF
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Upload Documents */}
          {!loading && activeTab === 'upload' && (
            <div>
              <h2 className="sp-section-title">Upload Your Documents</h2>
              <p className="sp-section-desc">Upload your compliance documents (W-9, insurance, NDA, etc.) for the company to review.</p>
              <SupplierDocumentUpload onUploaded={() => { fetchMyDocs(); setActiveTab('my-docs'); }} />
            </div>
          )}

          {/* My Documents */}
          {!loading && activeTab === 'my-docs' && (
            <div>
              <h2 className="sp-section-title">My Documents</h2>
              {myDocs.length === 0 ? (
                <div className="sp-empty-state">
                  <span>📁</span>
                  <p>No documents uploaded yet.</p>
                </div>
              ) : (
                <table className="sp-table">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Type</th>
                      <th>Source</th>
                      <th>Uploaded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myDocs.map(d => (
                      <tr key={d.CompanyDocumentId}>
                        <td>
                          <a href={`${BASE_URL}${d.FileUrl}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3b5bfd' }}>
                            {d.Title || d.FileName}
                          </a>
                        </td>
                        <td><span className="sp-badge">{d.DocumentTypeCode}</span></td>
                        <td>
                          {d.SourceType === 'SUPPLIER_SIGNED' && <span className="sp-badge sp-badge-signed">✍️ Signed</span>}
                          {d.SourceType === 'SUPPLIER_PORTAL' && <span className="sp-badge sp-badge-portal">📁 Uploaded</span>}
                          {(!d.SourceType || d.SourceType === 'ADMIN') && <span className="sp-badge">Admin</span>}
                        </td>
                        <td style={{ color: '#6b7280', fontSize: '13px' }}>{new Date(d.CreatedAtUtc).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {/* PDF viewer + signature modal */}
      {viewingDoc && (
        <DocumentSignViewer
          signRequest={viewingDoc}
          onClose={() => setViewingDoc(null)}
          onSigned={() => {
            setViewingDoc(null);
            fetchPending();
            fetchSigned();
          }}
        />
      )}
    </div>
  );
};

export default SupplierPortal;
